/**
 * Plan-Based Feature Gating — 4-Tier Conversion-Optimized Model
 *
 * Organized around INVESTOR OUTCOMES, not software features.
 * Every feature answers: "How does this help me make more money?"
 *
 * Pricing Psychology:
 * - Starter ($39) = Anchor. Makes Investor feel affordable.
 * - Investor ($79) = The "Smart Choice." Most features, best value signal.
 * - Pro ($129) = Decoy. Makes Investor feel like a steal.
 * - Enterprise (Custom) = For teams. Creates aspiration.
 */

export type PlanTier = 'starter' | 'investor' | 'pro' | 'enterprise';

export const PLAN_ORDER: PlanTier[] = ['starter', 'investor', 'pro', 'enterprise'];

// ── Features organized by INVESTOR OUTCOME, not software category ──
export const PLAN_FEATURES = {
  // ═══════════════════════════════════════════════════
  // OUTCOME: Find Better Deals
  // ═══════════════════════════════════════════════════
  'parcel_search': {
    tier: 'starter',
    name: 'Parcel Search',
    outcome: 'Find Better Deals',
    description: 'Search every parcel in your target county by address, APN, or owner',
    roi: 'Discover off-market properties county GIS websites bury',
  },
  'ai_parcel_scoring': {
    tier: 'starter',
    name: 'PLP Opportunity Score',
    outcome: 'Find Better Deals',
    description: 'Instant 0-100 investment score powered by 7 growth signals',
    roi: 'Evaluate parcels in seconds instead of hours of manual research',
  },
  'score_breakdown': {
    tier: 'starter',
    name: '5-Factor Score Breakdown',
    outcome: 'Find Better Deals',
    description: 'See exactly why a parcel scores high: Growth, Infrastructure, Utilities, Access, Risk',
    roi: 'Know which factors drive value before you buy',
  },
  'natural_language_search': {
    tier: 'investor',
    name: 'Natural Language Search',
    outcome: 'Find Better Deals',
    description: 'Type "5+ acres near Charlotte with water access" and get matching parcels',
    roi: 'Find specific opportunities 10x faster than browsing county records',
  },
  'advanced_ai_recommendations': {
    tier: 'pro',
    name: 'AI Deal Recommendations',
    outcome: 'Find Better Deals',
    description: 'Kestovar AI suggests parcels matching your investment criteria automatically',
    roi: 'Never miss a deal. AI watches 24/7 and surfaces opportunities you would have overlooked',
  },

  // ═══════════════════════════════════════════════════
  // OUTCOME: Reduce Risk
  // ═══════════════════════════════════════════════════
  'saved_parcels': {
    tier: 'starter',
    name: 'Saved Parcels',
    outcome: 'Reduce Risk',
    description: 'Save and organize parcels for later review',
    roi: 'Build a shortlist instead of losing track of opportunities',
  },
  'basic_reports': {
    tier: 'starter',
    name: 'Basic Property Reports',
    outcome: 'Reduce Risk',
    description: 'Generate property summary reports with key investment indicators',
    roi: 'Document your due diligence for lenders and partners',
  },
  'historical_intelligence': {
    tier: 'investor',
    name: '5-Year Historical Intelligence',
    outcome: 'Reduce Risk',
    description: 'See 5 years of parcel and county history: sales, permits, zoning changes',
    roi: 'Spot trends before they show up in listing prices',
  },
  'growth_trends': {
    tier: 'investor',
    name: 'Growth Trend Indicators',
    outcome: 'Reduce Risk',
    description: 'Population, development, and land price trend analysis by county',
    roi: 'Invest in growing counties, avoid declining ones',
  },
  'infrastructure_timeline': {
    tier: 'investor',
    name: 'Infrastructure Timeline',
    outcome: 'Reduce Risk',
    description: 'Track roads, utilities, schools, and hospitals being built near parcels',
    roi: 'Buy before infrastructure announcements drive prices up',
  },
  'alerts': {
    tier: 'investor',
    name: 'Smart Alerts',
    outcome: 'Reduce Risk',
    description: 'Get notified when parcels match your criteria or prices change',
    roi: 'React to opportunities in minutes, not weeks',
  },
  'investment_memo': {
    tier: 'investor',
    name: 'Investment Memorandum',
    outcome: 'Reduce Risk',
    description: 'Auto-generated professional investment memos for due diligence',
    roi: 'Present deals to lenders, partners, and investors with professional-grade analysis',
  },
  'market_analytics': {
    tier: 'pro',
    name: 'Market Analytics',
    outcome: 'Reduce Risk',
    description: 'Deep market analysis with predictive indicators and comp tracking',
    roi: 'Make data-backed decisions instead of gut feelings',
  },
  'county_comparison': {
    tier: 'pro',
    name: 'County Comparison',
    outcome: 'Reduce Risk',
    description: 'Side-by-side county analytics and ranking across your target markets',
    roi: 'Compare 10 counties in minutes instead of weeks of manual research',
  },

  // ═══════════════════════════════════════════════════
  // OUTCOME: Save Time
  // ═══════════════════════════════════════════════════
  'search_history': {
    tier: 'starter',
    name: 'Search History',
    outcome: 'Save Time',
    description: 'View and replay recent parcel searches',
    roi: 'Never redo the same search twice',
  },
  'csv_export': {
    tier: 'investor',
    name: 'CSV Export',
    outcome: 'Save Time',
    description: 'Export parcel data to CSV for spreadsheet analysis',
    roi: 'Analyze 100 parcels in Excel in the time it takes to research 5 manually',
  },
  'pdf_export': {
    tier: 'investor',
    name: 'PDF Export',
    outcome: 'Save Time',
    description: 'Generate professional PDF reports for sharing',
    roi: 'Share due diligence with partners in one click',
  },
  'bulk_exports': {
    tier: 'pro',
    name: 'Bulk Exports (1,000)',
    outcome: 'Save Time',
    description: 'Export up to 1,000 parcels at once with full data',
    roi: 'Download entire county datasets for offline analysis',
  },
  'custom_templates': {
    tier: 'pro',
    name: 'Custom Report Templates',
    outcome: 'Save Time',
    description: 'Create and save custom report layouts for your workflow',
    roi: 'Generate consistent branded reports in seconds',
  },
  'advanced_reporting': {
    tier: 'pro',
    name: 'Advanced Reporting',
    outcome: 'Save Time',
    description: 'Comprehensive reports with charts, comps, and recommendations',
    roi: 'Produce lender-ready reports without hiring an analyst',
  },

  // ═══════════════════════════════════════════════════
  // OUTCOME: Monitor Investments
  // ═══════════════════════════════════════════════════
  'multiple_watchlists': {
    tier: 'investor',
    name: 'Multiple Watchlists',
    outcome: 'Monitor Investments',
    description: 'Create up to 10 county watchlists with custom filters',
    roi: 'Track every market you invest in from one dashboard',
  },
  'portfolio_management': {
    tier: 'pro',
    name: 'Portfolio Management',
    outcome: 'Monitor Investments',
    description: 'Track and analyze your land portfolio across all counties',
    roi: 'See your entire portfolio performance in one view',
  },
  'infrastructure_overlay': {
    tier: 'investor',
    name: 'Infrastructure Overlays',
    outcome: 'Monitor Investments',
    description: 'Visualize roads, utilities, and infrastructure on the interactive map',
    roi: 'See exactly what infrastructure exists — or is coming — near your parcels',
  },

  // ═══════════════════════════════════════════════════
  // OUTCOME: Team Collaboration (Enterprise)
  // ═══════════════════════════════════════════════════
  'team_management': {
    tier: 'enterprise',
    name: 'Team Management',
    outcome: 'Team Collaboration',
    description: 'Manage team members and their access levels',
    roi: 'Scale your investment operation without sharing logins',
  },
  'role_permissions': {
    tier: 'enterprise',
    name: 'Role Permissions',
    outcome: 'Team Collaboration',
    description: 'Granular access control with custom roles',
    roi: 'Analysts see research. Partners see reports. You see everything.',
  },
  'org_dashboard': {
    tier: 'enterprise',
    name: 'Organization Dashboard',
    outcome: 'Team Collaboration',
    description: 'Company-wide analytics and usage reports',
    roi: 'See which markets your team is finding the most opportunities in',
  },
  'audit_history': {
    tier: 'enterprise',
    name: 'Audit History',
    outcome: 'Team Collaboration',
    description: 'Complete audit trail of all team actions',
    roi: 'Track every decision for compliance and accountability',
  },

  // ═══════════════════════════════════════════════════
  // OUTCOME: Enterprise Management
  // ═══════════════════════════════════════════════════
  'api_access': {
    tier: 'enterprise',
    name: 'API Access',
    outcome: 'Enterprise Management',
    description: 'Programmatic access to all Parcel Lead Pro data',
    roi: 'Integrate parcel intelligence into your existing tools and workflows',
  },
  'webhooks': {
    tier: 'enterprise',
    name: 'Webhooks',
    outcome: 'Enterprise Management',
    description: 'Real-time event notifications to your systems',
    roi: 'Trigger actions in your CRM, Slack, or internal tools automatically',
  },
  'dedicated_support': {
    tier: 'enterprise',
    name: 'Dedicated Account Manager',
    outcome: 'Enterprise Management',
    description: 'Priority support with a dedicated account manager',
    roi: 'Get answers in hours, not days. Your success is our job.',
  },
  'white_label': {
    tier: 'enterprise',
    name: 'White-Label Reports',
    outcome: 'Enterprise Management',
    description: 'Custom branded reports for your clients',
    roi: 'Present analysis under your brand to clients and stakeholders',
  },
} as const;

export type FeatureKey = keyof typeof PLAN_FEATURES;

/**
 * Group features by investor outcome category
 */
export function getFeaturesByOutcome(): Record<string, { key: FeatureKey; name: string; description: string; roi: string; tier: PlanTier }[]> {
  const outcomes: Record<string, { key: FeatureKey; name: string; description: string; roi: string; tier: PlanTier }[]> = {};

  (Object.entries(PLAN_FEATURES) as [FeatureKey, typeof PLAN_FEATURES[FeatureKey]][]).forEach(([key, config]) => {
    const outcome = config.outcome;
    if (!outcomes[outcome]) outcomes[outcome] = [];
    outcomes[outcome].push({ key, name: config.name, description: config.description, roi: config.roi, tier: config.tier });
  });

  return outcomes;
}

/**
 * Check if a plan tier has access to a given feature
 */
export function hasFeature(plan: PlanTier, feature: FeatureKey): boolean {
  const requiredTier = PLAN_FEATURES[feature]?.tier;
  if (!requiredTier) return false;
  return PLAN_ORDER.indexOf(plan) >= PLAN_ORDER.indexOf(requiredTier);
}

/**
 * Get the minimum plan tier required for a feature
 */
export function getRequiredPlan(feature: FeatureKey): PlanTier | null {
  return PLAN_FEATURES[feature]?.tier ?? null;
}

/**
 * Get all features available for a given plan tier
 */
export function getAvailableFeatures(plan: PlanTier): FeatureKey[] {
  return (Object.entries(PLAN_FEATURES) as [FeatureKey, typeof PLAN_FEATURES[FeatureKey]][])
    .filter(([, config]) => PLAN_ORDER.indexOf(plan) >= PLAN_ORDER.indexOf(config.tier))
    .map(([key]) => key);
}

/**
 * Get features NOT available for a given plan tier — with ROI messaging
 */
export function getGatedFeatures(plan: PlanTier): { feature: FeatureKey; name: string; description: string; roi: string; outcome: string; requiredPlan: PlanTier }[] {
  return (Object.entries(PLAN_FEATURES) as [FeatureKey, typeof PLAN_FEATURES[FeatureKey]][])
    .filter(([, config]) => PLAN_ORDER.indexOf(plan) < PLAN_ORDER.indexOf(config.tier))
    .map(([key, config]) => ({
      feature: key,
      name: config.name,
      description: config.description,
      roi: config.roi,
      outcome: config.outcome,
      requiredPlan: config.tier,
    }));
}

/**
 * Get upgrade value message for a feature lock
 * Returns psychologically compelling reason to upgrade
 */
export function getUpgradeValue(feature: FeatureKey): { what: string; why: string; benefit: string } {
  const f = PLAN_FEATURES[feature];
  if (!f) return { what: 'This feature', why: 'Unlock more capabilities', benefit: 'Get more from Parcel Lead Pro' };
  return {
    what: f.name,
    why: f.roi,
    benefit: f.description,
  };
}

/**
 * Get tier display name
 */
export function getTierLabel(tier: PlanTier): string {
  switch (tier) {
    case 'starter': return 'Starter';
    case 'investor': return 'Investor';
    case 'pro': return 'Pro';
    case 'enterprise': return 'Enterprise';
  }
}

/**
 * Get tier display color
 */
export function getTierColor(tier: PlanTier): string {
  switch (tier) {
    case 'starter': return '#2F80ED';
    case 'investor': return '#7CFF6B';
    case 'pro': return '#7CFF6B';
    case 'enterprise': return '#A855F7';
  }
}

/**
 * Get monthly search limit for a plan tier
 */
export function getSearchLimit(plan: PlanTier): number {
  switch (plan) {
    case 'starter': return 25;
    case 'investor': return 200;
    case 'pro': return 500;
    case 'enterprise': return Infinity;
  }
}

/**
 * Get watchlist limit for a plan tier
 */
export function getWatchlistLimit(plan: PlanTier): number {
  switch (plan) {
    case 'starter': return 1;
    case 'investor': return 10;
    case 'pro': return 25;
    case 'enterprise': return Infinity;
  }
}

/**
 * Get parcel save limit for a plan tier
 */
export function getParcelSaveLimit(plan: PlanTier): number {
  switch (plan) {
    case 'starter': return 50;
    case 'investor': return 500;
    case 'pro': return 2000;
    case 'enterprise': return Infinity;
  }
}

/**
 * Convert any plan name to canonical PlanTier
 */
export function normalizePlan(plan: string): PlanTier {
  const mapping: Record<string, PlanTier> = {
    'starter': 'starter',
    'investor': 'investor',
    'pro': 'pro',
    'enterprise': 'enterprise',
    'growth': 'investor',
    'professional': 'investor',
    'free': 'starter',
    'trial': 'starter',
  };
  return mapping[plan.toLowerCase()] || 'starter';
}

/**
 * Get plan pricing with CONVERSION-OPTIMIZED copy
 * Every description answers: "What do I GET for this money?"
 */
export function getPlanPricing(plan: PlanTier): {
  name: string;
  price: string;
  period: string;
  searches: string;
  description: string;
  valueProp: string;
  socialProof: string;
} {
  switch (plan) {
    case 'starter':
      return {
        name: 'Starter',
        price: '$39',
        period: '/mo',
        searches: '25/mo',
        description: 'Validate one county before you scale',
        valueProp: 'Find and score parcels in your target county',
        socialProof: 'Perfect for first-time land investors',
      };
    case 'investor':
      return {
        name: 'Investor',
        price: '$79',
        period: '/mo',
        searches: '200/mo',
        description: 'Build weekly lead lists across multiple counties',
        valueProp: 'Everything you need to find, evaluate, and track deals',
        socialProof: 'Built for active deal hunters',
      };
    case 'pro':
      return {
        name: 'Pro',
        price: '$129',
        period: '/mo',
        searches: '500/mo',
        description: 'Manage a multi-county portfolio at scale',
        valueProp: 'Data-powered deal discovery + portfolio intelligence',
        socialProof: 'For professionals managing 10+ parcels',
      };
    case 'enterprise':
      return {
        name: 'Enterprise',
        price: 'Custom',
        period: '',
        searches: 'Unlimited',
        description: 'Team-wide deployment with API access',
        valueProp: 'Custom integrations, team seats, dedicated support',
        socialProof: 'Used by land acquisition teams and developers',
      };
  }
}

/**
 * Get plan features grouped by INVESTOR OUTCOME for display
 * This replaces the old software-category grouping
 */
export function getPlanFeaturesByOutcome(plan: PlanTier): {
  outcome: string;
  features: { name: string; included: boolean; highlight?: string }[];
}[] {
  const allOutcomes = getFeaturesByOutcome();
  const result: { outcome: string; features: { name: string; included: boolean; highlight?: string }[] }[] = [];

  const outcomeOrder = ['Find Better Deals', 'Reduce Risk', 'Save Time', 'Monitor Investments', 'Team Collaboration', 'Enterprise Management'];

  for (const outcome of outcomeOrder) {
    const features = allOutcomes[outcome];
    if (!features) continue;

    const featureList = features.map(f => ({
      name: f.name,
      included: PLAN_ORDER.indexOf(plan) >= PLAN_ORDER.indexOf(f.tier),
      highlight: f.roi,
    }));

    // Only show outcomes that have at least one feature in this plan
    // OR outcomes that have a feature in a higher tier (to show what they're missing)
    const hasAny = featureList.some(f => f.included) || plan !== 'enterprise';
    if (hasAny) {
      result.push({ outcome, features: featureList });
    }
  }

  return result;
}
